// Offline Sync Hook - Manages background synchronization
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  initOfflineStorage,
  savePendingPost,
  getPendingPosts as fetchPendingPosts,
  removePendingPost,
  updatePendingPostStatus,
  cachePosts,
  getCachedPosts,
  getPendingActions,
  removePendingAction,
  isOnline
} from '@/services/offlineStorage';

// Debug logging (disabled in production)
const DEBUG = process.env.NODE_ENV === 'development';
const log = (...args) => DEBUG && console.log(...args);

export const useOfflineSync = () => {
  const { api, token, user } = useAuth();
  const [online, setOnline] = useState(isOnline());
  const [syncing, setSyncing] = useState(false);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const syncIntervalRef = useRef(null);

  // Computed values
  const isOffline = !online;
  const isSyncing = syncing;
  const pendingCount = pendingPosts.length + pendingActions.length;
  const hasPendingItems = pendingCount > 0;

  // Initialize offline storage
  useEffect(() => {
    initOfflineStorage().catch(log);
    loadPendingItems();
  }, []);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => {
      log('[Sync] Back online');
      setOnline(true);
      toast.success('Connexion rétablie !');
      // Trigger sync when back online
      syncPendingItems();
    };

    const handleOffline = () => {
      log('[Sync] Gone offline');
      setOnline(false);
      toast.warning('Mode hors ligne activé', {
        description: 'Vos modifications seront synchronisées à la reconnexion.'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load pending items from IndexedDB
  const loadPendingItems = useCallback(async () => {
    try {
      const posts = await fetchPendingPosts();
      const actions = await getPendingActions();
      setPendingPosts(posts || []);
      setPendingActions(actions || []);
    } catch (error) {
      log('[Sync] Failed to load pending items:', error);
      setPendingPosts([]);
      setPendingActions([]);
    }
  }, []);

  // Save post for offline sync
  const savePostOffline = useCallback(async (postData) => {
    try {
      const savedPost = await savePendingPost({
        ...postData,
        user_id: user?.user_id,
        user_name: user?.name,
        user_picture: user?.picture
      });
      
      setPendingPosts(prev => [...prev, savedPost]);
      
      toast.info('Post sauvegardé hors ligne', {
        description: 'Il sera publié automatiquement à la reconnexion.'
      });
      
      return savedPost;
    } catch (error) {
      log('[Sync] Failed to save post offline:', error);
      throw error;
    }
  }, [user]);

  // Sync a single post
  const syncPost = useCallback(async (post) => {
    if (!api || !token) return false;
    
    try {
      const postPayload = {
        title: post.title,
        content: post.content,
        category: post.category,
        commune: post.commune,
        quartier: post.quartier || null,
        image_url: post.image_url || null,
        audio_url: post.audio_url || null,
        event_date: post.event_date || null,
        poll_options: post.poll_options || null,
        poll_end_date: post.poll_end_date || null
      };

      await api.post('/posts', postPayload);
      await removePendingPost(post.local_id);
      return true;
    } catch (error) {
      await updatePendingPostStatus(post.local_id, 'failed', error.message);
      return false;
    }
  }, [api, token]);

  // Sync a single action
  const syncAction = useCallback(async (action) => {
    if (!api || !token) return false;
    
    try {
      switch (action.type) {
        case 'like':
          await api.post(`/posts/${action.data.post_id}/like`);
          break;
        case 'comment':
          await api.post(`/posts/${action.data.post_id}/comments`, {
            content: action.data.content
          });
          break;
        case 'follow':
          await api.post(`/users/${action.data.user_id}/follow`);
          break;
        default:
          log(`[Sync] Unknown action type: ${action.type}`);
          return false;
      }

      await removePendingAction(action.action_id);
      return true;
    } catch (error) {
      log(`[Sync] Failed to sync action ${action.type}:`, error);
      return false;
    }
  }, [api, token]);

  // Sync all pending items
  const syncPendingItems = useCallback(async () => {
    if (!online || syncing) return;

    setSyncing(true);
    log('[Sync] Starting sync...');

    try {
      const posts = await fetchPendingPosts();
      const actions = await getPendingActions();
      
      let syncedPosts = 0;
      let syncedActions = 0;
      let failedPosts = 0;

      // Sync posts
      for (const post of posts) {
        if (post.status === 'pending' || (post.status === 'failed' && post.retry_count < 3)) {
          const success = await syncPost(post);
          if (success) {
            syncedPosts++;
          } else {
            failedPosts++;
          }
        }
      }

      // Sync actions
      for (const action of actions) {
        const success = await syncAction(action);
        if (success) {
          syncedActions++;
        }
      }

      // Show summary
      if (syncedPosts > 0 || syncedActions > 0) {
        const messages = [];
        if (syncedPosts > 0) messages.push(`${syncedPosts} post(s)`);
        if (syncedActions > 0) messages.push(`${syncedActions} action(s)`);
        
        toast.success('Synchronisation terminée', {
          description: `${messages.join(' et ')} synchronisé(s)`
        });
      }

      if (failedPosts > 0) {
        toast.error(`${failedPosts} post(s) n'ont pas pu être synchronisés`);
      }

      // Reload pending items
      await loadPendingItems();
    } catch (error) {
      log('[Sync] Sync failed:', error);
    } finally {
      setSyncing(false);
    }
  }, [online, syncing, syncPost, syncAction, loadPendingItems]);

  // Retry syncing a specific post
  const retryPostSync = useCallback(async (localId) => {
    const post = pendingPosts.find(p => p.local_id === localId);
    if (!post || !online) return;

    setSyncing(true);
    try {
      const success = await syncPost(post);
      if (success) {
        toast.success('Post synchronisé !');
        await loadPendingItems();
      } else {
        toast.error('Échec de la synchronisation');
      }
    } catch (error) {
      toast.error('Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  }, [pendingPosts, online, syncPost, loadPendingItems]);

  // Delete a pending post
  const deletePendingPostById = useCallback(async (localId) => {
    try {
      await removePendingPost(localId);
      setPendingPosts(prev => prev.filter(p => p.local_id !== localId));
      toast.success('Brouillon supprimé');
    } catch (error) {
      log('[Sync] Failed to delete pending post:', error);
    }
  }, []);

  // Cache posts for offline reading
  const cachePostsForOffline = useCallback(async (posts) => {
    try {
      await cachePosts(posts);
    } catch (error) {
      log('[Sync] Failed to cache posts:', error);
    }
  }, []);

  // Get cached posts when offline
  const getOfflinePosts = useCallback(async (commune = null) => {
    try {
      return await getCachedPosts(commune);
    } catch (error) {
      log('[Sync] Failed to get cached posts:', error);
      return [];
    }
  }, []);

  // Create post (with offline support) - legacy API
  const createPostOffline = useCallback(async (postData) => {
    if (online && api) {
      try {
        return await api.post('/posts', postData);
      } catch (error) {
        if (error.message !== 'Network Error') {
          throw error;
        }
      }
    }

    const pendingPost = await savePostOffline(postData);
    return {
      data: {
        ...pendingPost,
        post_id: pendingPost.local_id,
        _offline: true
      }
    };
  }, [online, api, savePostOffline]);

  // Set up periodic sync check
  useEffect(() => {
    // Sync check every 30 seconds when online
    syncIntervalRef.current = setInterval(() => {
      if (online && !syncing && hasPendingItems) {
        syncPendingItems();
      }
    }, 30000);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [online, syncing, hasPendingItems, syncPendingItems]);

  // Expose both old and new API for compatibility
  return {
    // New API (used by CreatePostPage)
    isOffline,
    pendingPosts,
    pendingActions,
    isSyncing,
    hasPendingItems,
    pendingCount,
    savePostOffline,
    syncPendingItems,
    retryPostSync,
    deletePendingPostById,
    loadPendingItems,
    
    // Legacy API (backwards compatibility)
    online,
    syncing,
    syncAll: syncPendingItems,
    cachePostsForOffline,
    getOfflinePosts,
    createPostOffline,
    getPendingPosts: loadPendingItems,
    updatePendingCount: loadPendingItems
  };
};

export default useOfflineSync;
